import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAbility } from '@casl/react';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslate } from '@bynder/localization';
import { AbilityContext } from '../../abilities/Can';
import { CSRF } from '../../helpers/csrf';
import { API, getAPISpotlightCalls, handleSpotlightResults } from '../../helpers/api';
import {
	Highlight,
	MetapropertiesOption,
	SearchResults,
	SpotlightMediaSearchInCollectionResponse,
	SpotlightMediaSearchResponse,
	SpotlightSearchParams,
} from '../../types';
import { useRecentSearches } from './hooks/useRecentSearches';
import { isModule } from '../../helpers/windowLocation';
import { initStickySearch } from './legacy/initStickySearch';
import { encodeUUID } from '../../helpers/id';
import { SearchByImageModal } from './Modal/SearchByImageModal';
import { useIsFetchingAssets } from './hooks/useIsFetchingAssets';
import { ResultsDropdown } from './ResultsDropdown/ResultsDropdown';
import {
	executeSearch,
	getSearchParamsField,
	isReactAssetOverviewActive,
	onMetapropertyOptionEnter,
	onTagSearch,
	resetStickySearchInAssetbank,
} from './helpers/searchUtils';
import { EMPTY_RESULTS, SEARCH_MIN_CHARS } from './constants';
import { ClearButton } from './IconsAndButtons/ClearButton';
import { LoadingIcon } from './IconsAndButtons/LoadingIcon';
import { SearchIcon } from './IconsAndButtons/SearchIcon';
import { useNLSSearch } from './NLSSearch/useNLSSearch';
import { emitSimilaritySearchEvent } from './NLSSearch/utils';
import type { SearchParams } from './helpers/types';
import { useCustomEvent } from '../../helpers/useCustomEvent';
import usePrevious from '../../helpers/usePrevious';
import { useCanUseAiFeatures } from './NLSSearch/useCanUseAiFeatures';
import { useSearchItemKeyboardNavigation } from './hooks/useSearchItemKeyboardNavigation';

const SEARCH_DEBOUNCE_ms = 500;

function calculateTotalResults(results: SearchResults): number {
	if (!results) {
		return 0;
	}

	return Object.keys(results).reduce((acc, key) => {
		const items = results[key as keyof SearchResults];
		const itemsTotal = Array.isArray(items) ? items.length : items?.total;

		return acc + itemsTotal;
	}, 0);
}

export function Spotlight() {
	const { translate } = useTranslate();
	const refInput = useRef<HTMLInputElement>(null);
	const ability = useAbility(AbilityContext);
	const { onNewRecentSearch } = useRecentSearches();

	const [searchTerm, setSearchTerm] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [searchResults, setSearchResults] = useState<SearchResults>(EMPTY_RESULTS);

	const [showDropdownResults, setShowDropdownResults] = useState(false);
	const [showNLSBanner, setShowNLSBanner] = useState(false);

	const [highLight, setHighLight] = useState<Highlight>({});

	const isCollectionModule = isModule('collections');
	const collectionId =
		encodeUUID(window.location.pathname.split('/')[3]) ||
		window.bynder.propsAssetOverview?.collectionId;
	const sharedCollectionId = window.bynder.propsAssetOverview?.collectionRecipientShareId;
	const isSharedCollection = isModule('share') || sharedCollectionId;

	const previousSearchTerm = usePrevious(searchTerm);

	const canShowStickySearch = ability.can('show', 'sticky_search');
	const canShowRecentSearches = ability.can('show', 'spotlight_recent_searches');
	const canShowBrandguidelines =
		ability.can('show', 'brandguidelines') || ability.can('show', 'homepage');

	const { canUseNLSSearch, isNLSSearchOn, setIsNLSSearchOn } = useNLSSearch(canShowStickySearch);
	const isPerformingNLSSearch = canUseNLSSearch && isNLSSearchOn;

	const { canUseSimilaritySearch } = useCanUseAiFeatures();
	const { isFetchingAssets } = useIsFetchingAssets();

	useCustomEvent('headerFrontend_clearSpotlightInput', () => setSearchTerm(''));

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (e.target !== refInput.current) {
				setShowDropdownResults(false);
				setShowNLSBanner(false);
			}
		};

		document.addEventListener('click', handleClickOutside);

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (canShowStickySearch) {
			const stickySearchTerm = initStickySearch();
			setSearchTerm(stickySearchTerm);
		}
	}, [canShowStickySearch]);

	const setHighlightFromResponse = (value?: Highlight) => {
		if (!value) {
			return;
		}

		setHighLight(value);
	};

	const showLoadingIcon =
		isLoading && !isPerformingNLSSearch && searchTerm.length >= SEARCH_MIN_CHARS;
	const showClearButton = searchTerm.length > 0 && (!isLoading || isPerformingNLSSearch);

	const searchDebounced = useDebouncedCallback(async (term: string) => {
		const isSearchingInsideACollection =
			collectionId || (isSharedCollection && sharedCollectionId);

		const params: SpotlightSearchParams | undefined =
			collectionId || (isSharedCollection && sharedCollectionId)
				? {
						filterKey: `collection_${collectionId}`,
						searchType: 'collection',
						collectionId: collectionId || '',
						...(sharedCollectionId && { recipientShareId: sharedCollectionId }),
				  }
				: undefined;

		const apiCalls = getAPISpotlightCalls(term, params, canShowBrandguidelines);

		const spotlightPromises = apiCalls.map(({ key, call }) =>
			call.then((response) => ({ key, response })),
		);

		const response = await API.searchSpotlightAssets(term, params);

		if (isSearchingInsideACollection) {
			const responseAsCollection = response as SpotlightMediaSearchInCollectionResponse;

			setSearchResults((prevResults: SearchResults) => ({
				...prevResults,
				assets: {
					items: responseAsCollection.results?.media?.docs,
					total: responseAsCollection.results?.media?.numFound,
				},
				tags: responseAsCollection.tags,
			}));

			if (responseAsCollection.highlighting) {
				setHighlightFromResponse(responseAsCollection.highlighting);
			}
		} else {
			const responseAsMedia = response as SpotlightMediaSearchResponse;

			setSearchResults((prevResults: SearchResults) => ({
				...prevResults,
				assets: {
					items: responseAsMedia.results,
					total: responseAsMedia.count,
				},
				tags: responseAsMedia.tags,
			}));

			if (responseAsMedia.highlighting) {
				setHighlightFromResponse(responseAsMedia.highlighting);
			}
		}

		setShowDropdownResults(true);

		const responses = await Promise.all(spotlightPromises);
		handleSpotlightResults(responses, setSearchResults);

		if (isLoading && searchTerm.length >= SEARCH_MIN_CHARS) {
			setShowDropdownResults(true);
		}

		setIsLoading(false);
	}, SEARCH_DEBOUNCE_ms);

	const search = useCallback(
		(term: string) => {
			if (term.length < SEARCH_MIN_CHARS) {
				return;
			}

			setIsLoading(true);
			searchDebounced(term);
		},
		[searchDebounced],
	);

	const onValueChange = (e: React.ChangeEvent<HTMLInputElement> | undefined) => {
		const value = e?.currentTarget.value || '';
		const isEmptyTerm = value.length === 0;

		setSearchTerm(value);
		setShowNLSBanner(true);

		if (isEmptyTerm || value.length < SEARCH_MIN_CHARS) {
			setShowDropdownResults(false);
			setIsLoading(false);
			setSearchResults(EMPTY_RESULTS);
		}

		if (previousSearchTerm && !value.length) {
			window.heap?.track('clear-spotlight-search-on-backspace');
		}

		search(value);

		if (canShowStickySearch && isEmptyTerm) {
			resetStickySearchInAssetbank(isPerformingNLSSearch);
		}
	};

	const clearResults = () => {
		setSearchTerm('');
		setSearchResults(EMPTY_RESULTS);

		if (canShowStickySearch) {
			resetStickySearchInAssetbank(isPerformingNLSSearch);
		}

		if (refInput.current) {
			refInput.current.focus();
		}
	};

	const onClearButtonClick = () => {
		clearResults();

		setShowDropdownResults(false);
		setShowNLSBanner(false);
	};

	const onDropdownClearButtonClick = () => {
		clearResults();
	};

	const onInputFocus = () => {
		const hasResults = calculateTotalResults(searchResults) > 0;

		const wasManuallyClosed = hasResults && !showDropdownResults;
		const isIncompleteSearch = searchTerm.length > 0 && searchTerm.length < SEARCH_MIN_CHARS;

		setShowNLSBanner(true);

		// Don't open results again, when the user has manually closed it by clicking outside
		if (wasManuallyClosed || isIncompleteSearch) {
			return;
		}

		if (searchTerm.length === 0) {
			setShowDropdownResults(true);

			return;
		}

		search(searchTerm);
	};

	const defaultSearchAction = (
		isMPOSearch?: boolean,
		metapropertyName?: string,
		metapropertyOptionName?: string,
		metapropertyOptionLabel?: string,
		// eslint-disable-next-line max-params
	) => {
		if (searchTerm.length < SEARCH_MIN_CHARS) {
			return;
		}

		if (isPerformingNLSSearch && isReactAssetOverviewActive()) {
			const skipAddingToFilterbar = canShowStickySearch;

			emitSimilaritySearchEvent(
				{
					similarToAssetIds: null,
					similarToImageData: null,
					similarToURL: null,
					similarToText: searchTerm,
				},
				skipAddingToFilterbar,
			);

			return;
		}

		const params: SearchParams = {
			field: getSearchParamsField(canShowStickySearch, isMPOSearch, metapropertyName),
			value: isMPOSearch && metapropertyOptionName ? metapropertyOptionName : searchTerm,
			filterType: 'add',
			filterKey: isMPOSearch ? 'savedFilters' : '',
			...(isPerformingNLSSearch ? { similarToText: searchTerm } : {}),
		};

		if (canShowRecentSearches) {
			onNewRecentSearch(searchTerm);
		}

		executeSearch(params, metapropertyOptionLabel, isPerformingNLSSearch);
	};

	const handleEnterKeyForItemInSpotlight = () => {
		// If there is an active search result, we trigger the click on it
		if (currentActiveSearchItem) {
			// This is for collections and guides we need to navigate on the page
			if (currentActiveSearchItem.path) {
				window.location.href = currentActiveSearchItem.path;
			}

			if (currentActiveSearchItem.id.startsWith('tag_')) {
				// For tags we need to do a search for it
				return onTagSearch(currentActiveSearchItem.name, clearResults);
			}

			if (currentActiveSearchItem.isMetapropertyOption) {
				// For metaproperty options we neeed to do a search for it
				const searchItemAsMetapropertyOption =
					currentActiveSearchItem as MetapropertiesOption;

				return onMetapropertyOptionEnter(
					searchItemAsMetapropertyOption,
					defaultSearchAction,
					clearResults,
				);
			}

			window.bynder?.tools?.MediaModal?.open(currentActiveSearchItem?.id);

			setShowDropdownResults(false);
			setShowNLSBanner(false);
		} else {
			defaultSearchAction();
		}
	};

	const handleEnterKey = () => {
		if (isLoading) {
			// Cancel results
			setIsLoading(false);
		}

		setShowDropdownResults(false);
		setShowNLSBanner(false);

		handleEnterKeyForItemInSpotlight();

		if (!canShowStickySearch) {
			setSearchTerm('');
		}
	};

	const { currentActiveSearchItem, onInputKeyUp } = useSearchItemKeyboardNavigation({
		searchResults,
		isFetchingAssets,
		showDropdownResults,
		handleEnterKey,
		setShowDropdownResults,
		setSearchTerm,
	});

	return (
		<div className="nav-bar-search">
			<SearchIcon isPerformingNLSSearch={isPerformingNLSSearch} />

			<form
				id="spotlightForm"
				name="nav-search"
				action="/search/set/"
				data-action="/search/spotlight/"
				method="post"
				onSubmitCapture={(event) => event.preventDefault()}
			>
				<input type="hidden" name="csrf" value={CSRF} />
				<input type="hidden" name="filterType" value="add" />

				{canShowStickySearch ? (
					<input type="hidden" name="field" value="singletext" />
				) : (
					<input type="hidden" name="field" value="text" />
				)}

				<input
					ref={refInput}
					id="focus-new"
					name="value"
					value={searchTerm}
					placeholder={
						isPerformingNLSSearch
							? translate('spotlight.searchSomethingNLS')
							: translate('spotlight.searchSomething')
					}
					className="search"
					autoComplete="off"
					onChange={onValueChange}
					onClick={onInputFocus}
					onFocus={onInputFocus}
					onKeyUp={onInputKeyUp}
				/>
				{isCollectionModule && collectionId && (
					<input type="hidden" name="collectionId" value={collectionId} />
				)}
			</form>

			{showClearButton && (
				<ClearButton moveToTheLeft={canUseSimilaritySearch} onClick={onClearButtonClick} />
			)}

			{showLoadingIcon && <LoadingIcon moveToTheLeft={canUseSimilaritySearch} />}

			{canUseSimilaritySearch && <SearchByImageModal />}

			<ResultsDropdown
				showMainSection={showDropdownResults && !isPerformingNLSSearch}
				results={searchResults}
				searchTerm={searchTerm}
				isLoading={isLoading}
				highLight={highLight}
				onClearSearch={onDropdownClearButtonClick}
				clearSearchTerm={() => setSearchTerm('')}
				defaultSearchAction={defaultSearchAction}
				isNLSSearchOn={isNLSSearchOn}
				setIsNLSSearchOn={setIsNLSSearchOn}
				showNLSBanner={canUseNLSSearch && showNLSBanner}
				spotlightInputRef={refInput}
				setSearchTerm={setSearchTerm}
				currentActiveSearchItem={currentActiveSearchItem}
			/>
		</div>
	);
}
